 import { useRef, useState, useEffect, useCallback } from "react";

export function useCamera() {
  const videoRef = useRef(null);
  const previewStreamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);

  /**
   * Attaches a media stream to the video element.
   * FIX: Uses onloadedmetadata to ensure video plays only after metadata is loaded,
   * preventing black screen issues in production environments.
   */
  const attachStreamToVideo = useCallback((stream) => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      // Wait for metadata to load before playing - fixes black screen issue
      videoRef.current.onloadedmetadata = () => {
        videoRef.current.play().catch(console.error);
      };
    }
  }, []);

  useEffect(() => {
    // Track component mount state to prevent state updates after unmount
    let mounted = true;

    const initMediaDevices = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });

        // Cleanup if component unmounted while waiting for stream
        if (!mounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        previewStreamRef.current = stream;
        // FIX: Assign full stream directly (not just video track) to prevent black screen
        attachStreamToVideo(stream);

        const hasVideo = stream.getVideoTracks().length > 0;
        const hasAudio = stream.getAudioTracks().length > 0;

        setCameraReady(hasVideo);
        setMicReady(hasAudio);

        if (!hasVideo || !hasAudio) {
          const errors = [];
          if (!hasVideo) errors.push("Camera not available");
          if (!hasAudio) errors.push("Microphone not available");
          setCameraError(errors.join(", "));
        }
      } catch (err) {
        console.error("Media access denied:", err);
        if (!mounted) return;
        if (err.name === "NotAllowedError") {
          setCameraError("Camera and microphone access denied");
        } else if (err.name === "NotFoundError") {
          setCameraError("Camera or microphone not found");
        } else {
          setCameraError("Failed to access camera and microphone");
        }
      }
    };

    initMediaDevices();

    // Cleanup: stop all tracks and mark as unmounted
    return () => {
      mounted = false;
      previewStreamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [attachStreamToVideo]);

  /**
   * Restores the camera preview after interview ends.
   * Reuses the existing stream to avoid re-requesting permissions.
   */
  const restorePreview = useCallback(() => {
    attachStreamToVideo(previewStreamRef.current);
  }, [attachStreamToVideo]);

  return {
    videoRef,
    previewStreamRef,
    cameraReady,
    micReady,
    cameraError,
    restorePreview,
  };
}
 